import React from 'react'
import { object } from 'prop-types'
import { graphql } from 'gatsby'

import CategoryList from '@/components/Category'
import SEO from '@/components/SEO'
import Layout from '@/layout'
import { SORT, FILTERS, URLS } from '@/constants'
import mediaQueries from '@/layout/media-queries'
import EnhancedListProvider from '@/context/EnhancedList'
import { canUseWindow } from '@/utils/browser'
import {
  getHeadline,
  getLabel,
  filterAppsForLicense,
  getDefaultCategoryFilter,
  checkIfCompatibilityExcluded,
} from '@/utils/products'
import {
  getPublishedApps,
  getInstalledAppsIds,
  getProtocolVersion,
} from '@/store/selectors'

const overrideStyles = {
  container: {
    marginBottom: '60px',

    [mediaQueries.smallScreen()]: {
      marginBottom: '0px',
    },
  },
}

const getCategoryItems = (items, state) => {
  if (items) {
    const installedAppsIds = getInstalledAppsIds(state)
    const protocolVersion = getProtocolVersion(state)

    return filterAppsForLicense(items, installedAppsIds, protocolVersion, null)
  } else {
    return getPublishedApps(state)
  }
}

const sortOptions = Object.values(SORT)

const Category = ({ data }) => {
  const { category, allApp, allCampaign } = data
  const isAllAppsCategory = category.name === URLS.APPS.LABEL

  const categoryInfo = {
    ...category,
    headline: getHeadline(category.headline),
    name: getLabel(category.name),
  }

  // I can't filter in graph_ql with interpolation so I have to filter it in js
  const categoryCampaign = allCampaign.nodes.filter(
    (campaign) => campaign.id === `category-${category.name.toLowerCase()}`
  )[0]

  const allApps = filterAppsForLicense(allApp.nodes).reduce((obj, app) => {
    obj[app.id] = app
    return obj
  }, {})

  const categoryApps = !isAllAppsCategory
    ? allApp.nodes.filter(
        (app) => category && app.category && category.id === app.category.id
      )
    : canUseWindow
    ? undefined
    : allApp.nodes

  const categoryFilterValue = getDefaultCategoryFilter(categoryInfo.id)

  const defaultFilters = checkIfCompatibilityExcluded(
    !isAllAppsCategory && !!categoryFilterValue
      ? {
          [FILTERS.CATEGORY.ID]: [categoryFilterValue],
        }
      : {}
  )

  const supportedFilters = [
    FILTERS.CATEGORY.ID,
    FILTERS.PAYMENT.ID,
    FILTERS.WIDGET_PLACEMENT.ID,
    'sortBy',
    FILTERS.COMPATIBILITY.ID,
  ]

  return (
    <EnhancedListProvider
      items={categoryApps}
      itemsFilter={getCategoryItems}
      defaultSortValue={SORT.RECOMMENDED.VALUE}
      defaultSortOptions={sortOptions}
      defaultFilters={defaultFilters}
      supportedFilters={supportedFilters}
    >
      <Layout
        overrideStyles={overrideStyles}
        category={categoryInfo}
        headerLabel={categoryInfo.name}
      >
        <SEO
          title={`${process.env.GATSBY_PRODUCT_LABEL} for ${category.name} | ${
            process.env.GATSBY_PRODUCT_LABEL
          } - ${category.name} ${
            category.name === URLS.APPS.LABEL ? ' ' : ' apps'
          } and integrations | Add chat to ${category.name} software`}
          description={
            category.headline ||
            `Extend your ${process.env.GATSBY_PRODUCT_LABEL} with powerful features to get your work done faster.`
          }
          path={isAllAppsCategory ? '/apps/' : `/apps/${category.slug}/`}
        />
        <CategoryList
          {...categoryInfo}
          allApps={allApps}
          campaign={categoryCampaign}
        />
      </Layout>
    </EnhancedListProvider>
  )
}

Category.propTypes = {
  data: object,
}

export default Category

export const query = graphql`
  query ($id: String!) {
    category(id: { eq: $id }) {
      id
      slug
      name
      headline
      description
    }

    allApp {
      nodes {
        id
        slug
        name
        product
        installations
        shortDescription
        category {
          id
          name
          slug
        }
        icons {
          id
          large
          original
          small
          title
        }
        customProps {
          appVideoUrl
          hiddenForBeta
          isService
          usingBeta
          categoryOrder
          isPremium
          order
        }
        hidden
        longDescription
        tutorialUrl
        payment {
          price
          frequency
          trialDays
          perAccount
        }
      }
    }

    allCampaign {
      nodes {
        id
        description
        name
        applications {
          id
        }
        attributes {
          tags
        }
      }
    }
  }
`
